/**
 * paths — the bind / agg / interpolation grammar (README §4.2).
 *
 * Reused VERBATIM from the cover_json interpreter
 * (`components/widget/covers/coverRender.js`) so the two IRs stay consistent:
 *
 *   "title"                      → dotted read off the render context
 *   "entries[0].name"            → indexed read
 *   "entries[].image_url"        → array projection
 *   "item.name"                  → the ITEM SCOPE a grid opens per row
 *   "{title}" / "AI grouped · {medCounts.summary}"  → string interpolation
 *   { "bind": "meds" } / { "$": "item.name" }   → the same dotted read,
 *                                  object form (`$` is the card-template form)
 *   { "agg": { op, of, field, where, suffix } } → aggregate
 *
 * Everything here is PURE: same context in ⇒ same value out. Selectors,
 * derives and the component kit all read through these three functions.
 */

/**
 * The roots a bind may start at. `item` is the per-row scope a grid opens
 * around its `card` template (and any node carrying `repeat`) and is therefore
 * NOT a key of the page context: without it in this set, "item.name" inside a
 * card would be read as a literal string and the card would print its own
 * binding.
 *
 * `props` and `data` are the same idea one layer up — the scope a COMPOSED
 * component (an L3 Domain composition, README §6.2) opens around its
 * `composition` body: `props.*` is the parameter interface the outer node
 * passed, `data.*` the row that outer node's `bind` resolved to. Both exist
 * only inside a composition, exactly as `item` exists only inside a template.
 */

/**
 * `signal` is the third scope of the same kind, and it exists only inside a
 * connection's `payload` map (§5.1): the RAW payload of the signal that is
 * firing. Without it the map could reshape anything on the page EXCEPT the one
 * value the interaction just produced — `TextComposer.submitted {text}` could
 * not become `host.openMainSession {prompt}`, so five blueprints round-tripped
 * their own text through `page_args` to rename one field. With it,
 * `{"prompt": {"$": "signal.text"}}` is the whole wire.
 */
/**
 * `index` is the fourth scope of the same kind, and the smallest: the ORDINAL
 * of the current `repeat` row. It exists because a design can key on position
 * rather than on content — prod's notes-taker paints its topic rail, its
 * avatars and its map edges from `TONES[i % 6]`, and the bind grammar could
 * read every field of a row except which row it was. It is a number, not a
 * path, so it is only ever useful with a transform: `map` for a small table,
 * `cycle` for a repeating ladder.
 */
export const SCOPE_ROOTS = new Set([
  'item', 'props', 'data', 'entries', 'settings', 'page_args', 'pending',
  'focusEntry', 'focusGrid', 'selectors', 'signal', 'index',
]);

/** The root segment of a dotted expression ("item.image_url" → "item"). */
export function pathRoot(expr) {
  return String(expr).split('.')[0].split('[')[0];
}

/**
 * A LOCAL-PRIVACY session id — the on-device (Privacy Mode) sessions the phone
 * mixes into the same feed as cloud + widget rows. The Flutter twin is
 * `isLocalPrivacySessionId` (local_agent_kernel.dart, prefix `local-`); a cloud
 * session id is a uuid and never matches. Web has no privacy runner in prod, so
 * this only ever fires in tests/studio — it keeps the card's provenance render
 * symmetric with Flutter (CONTRACT §10).
 */
export function isLocalPrivacySessionId(id) {
  return typeof id === 'string' && id.startsWith('local-');
}

/**
 * CONTRACT §13 — a pending / feed row's TERMINAL state, the web twin of Flutter
 * `viewIrPendingTerminalStatus` / `viewIrPendingStatusFailed` (shared.dart).
 * Lives here in the pure layer (no components) so `cards.jsx`, `collections.jsx`
 * and `feedback.jsx` read ONE definition — exporting these predicates from a
 * component file would trip the fast-refresh `only-export-components` rule.
 *
 *  - `terminalStatus` → the lowercased terminal status, or null if still running.
 *  - `isTerminal`     → running strip filter: drop ANY session that ended
 *                       (completed → real log row; failed/stopped/timeout →
 *                       durable feed Card), so the strip is running-only.
 *  - `terminalFailed` → the FAILED set only (stopped / failed / timeout / …), the
 *                       durable Card's red "!" — a completed session never draws it.
 */
const TERMINAL_FAILED_STATES = new Set(['failed', 'error', 'ended', 'stopped', 'timeout']);
const TERMINAL_STATES = new Set([...TERMINAL_FAILED_STATES, 'complete', 'completed', 'finished']);

// Job-by-Contract (#P3-S3) — the ONE contract-driven card. When the api-server
// feed stamps a row's `region` (its anchor JobContract's single DERIVED placement,
// schema v2 §5: in_progress | failed | list), that region is AUTHORITATIVE and
// SUPERSEDES the status/state heuristic below — a launch's card resolves from the
// contract, not a scraped status. A STRICT value check so a data row's contract
// `region` never collides with a blueprint NODE's layout `region` slot (which also
// takes values like 'list'): only these three, read off a DATA row, are honored;
// anything else ⇒ the pre-S3 heuristic verbatim (absent = byte-identical). The
// Flutter twin is `viewIrPendingTerminalStatus` (shared.dart).
const CONTRACT_REGIONS = new Set(['in_progress', 'failed', 'list']);

export function contractRegion(r) {
  const region = r?.region;
  return (typeof region === 'string' && CONTRACT_REGIONS.has(region)) ? region : null;
}

export function terminalStatus(r) {
  const region = contractRegion(r);
  if (region) {
    // in_progress ⇒ still running (spinner, null); failed ⇒ the durable "!";
    // list ⇒ delivered (a terminal, non-failed 'completed' — drops into the feed).
    if (region === 'in_progress') return null;
    return region === 'failed' ? 'failed' : 'completed';
  }
  const s = String(r?.status ?? r?.state ?? '').toLowerCase();
  if (r?.terminal === true) return s || 'completed';
  return TERMINAL_STATES.has(s) ? s : null;
}

export function isTerminal(r) {
  return terminalStatus(r) != null || r?.failed === true;
}

export function terminalFailed(r) {
  const s = terminalStatus(r);
  return s != null && TERMINAL_FAILED_STATES.has(s);
}

// Job-by-Contract V3e — the ONE authoritative entry-level lifecycle the card + the
// "Widget Session" badge render, derived server-side in a single place (SoT) and
// carried on the WidgetUserData DTO as `contract_lifecycle`. The kit maps it 1:1,
// never re-deriving from region/status when it is present. There is deliberately
// NO entry-level `blocked` — a blocked leg surfaces here as `error` (a per-job
// `blocked` is a /charlotte chain-tree concern only).
const LIFECYCLE_STATES = new Set(['queued', 'running', 'delivered', 'error', 'stopped']);

export function contractLifecycle(r) {
  const lc = r?.contract_lifecycle;
  if (typeof lc === 'string' && LIFECYCLE_STATES.has(lc)) return lc;
  // BC fallback for rows WITHOUT the field — a no-contract §13 row, or pre-V3e
  // contract data that still carries only `region`/`status`. This path can only
  // yield running/error/delivered; the finer queued/stopped need the explicit
  // field, so old data collapses to exactly today's three visuals.
  const region = contractRegion(r);
  if (region === 'in_progress') return 'running';
  if (region === 'failed') return 'error';
  if (region === 'list') return 'delivered';
  if (terminalFailed(r)) return 'error';
  return null;
}

/**
 * THE number formatter — the whole kit's single site for turning a number into
 * display text. It lives here, in the pure layer, because BOTH sides need it:
 * a component renders through `fmt()` (which delegates here), and a derive
 * emits preformatted `*_label` fields for the text a blueprint interpolates
 * into its own copy. One formatter is what stops the same value from reading
 * "1770" in a page title and "1,770" in the chart underneath it.
 *
 * `null` is UNKNOWN and renders as an em dash, never "0".
 *
 * A value BELOW 1 keeps two decimals. Rounding to the tenth is right for the
 * counts this started as (1770 cal, 4 pages) and a LIE for every ratio: `0.96`
 * came out as "1", i.e. "complete", on a page whose whole point was that it was
 * not. Nothing in (0,1) may flatten to 1 — and a value too small for two
 * decimals falls back to one significant digit rather than reading "0".
 */
export function formatNumber(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  // en-US pinned (not device locale): the cross-runtime identity contract needs
  // the same feed to render identically on iOS/Web, and Flutter won't match a
  // per-device JS locale anyway. Matches the dates policy (DATE_LOCALE).
  if (Math.abs(n) >= 1000) return n.toLocaleString('en-US');
  if (n !== 0 && Math.abs(n) < 1) {
    const two = Math.round(n * 100) / 100;
    return String(two === 0 ? Number(n.toPrecision(1)) : two);
  }
  return String(Math.round(n * 10) / 10);
}

/** Coerce anything to a finite number (strips units: "320 cal" → 320). */
export function num(x) {
  if (typeof x === 'number') return Number.isFinite(x) ? x : 0;
  const n = parseFloat(String(x == null ? '' : x).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Dotted/indexed path read. `expr` segments may carry `[n]` (index) or `[]`
 * (project the rest of the path over the array). Returns undefined for any
 * miss — callers decide whether that is a literal or an empty.
 */
export function getPath(obj, expr) {
  if (obj == null || !expr) return undefined;
  const parts = String(expr).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length; i++) {
    const m = parts[i].match(/^([^[]*)(\[(\d*)\])?$/);
    if (!m) return undefined;
    const key = m[1];
    if (key) {
      if (cur == null) return undefined;
      cur = cur[key];
    }
    if (m[2] !== undefined) {
      if (!Array.isArray(cur)) return undefined;
      if (m[3] === '') {
        const rest = parts.slice(i + 1).join('.');
        return cur.map((x) => (rest ? getPath(x, rest) : x));
      }
      cur = cur[parseInt(m[3], 10)];
    }
  }
  return cur;
}

/**
 * A regex WRITTEN AS DATA. Always case-insensitive: every classifier a
 * blueprint ports (prod's `modeOf`, `statusKind`) lowercases before it tests,
 * and a `where` clause that silently missed on "Meeting" vs "meeting" would be
 * the same casing bug `mapValue` already guards against. An unparseable source
 * compiles to null and the clause is FALSE — a filter nobody can read must not
 * quietly pass everything.
 */
function compileMatcher(src) {
  try {
    return new RegExp(String(src), 'i');
  } catch {
    return null;
  }
}

/**
 * ONE `where` value against one read — the whole matching vocabulary.
 *
 *   "meeting"                     equal (string-loose, as it always was)
 *   ["meeting", "lecture"]        member of
 *   null                          absent (null / undefined / "")
 *   {"matches": "meeting|sync"}   regex, case-insensitive
 *   {"not": <any of the above>}   the negation of the same rule
 *
 * The two object forms are what a ported classifier needs and an exact-match
 * table cannot express: prod's mode chips are `/meeting|standup|sync|1:1/`
 * over `briefing_mode || source_type`, and prod's archive shelf is "every note
 * EXCEPT the one open" — a `filter` that can only say "equals" writes the
 * focused note into its own "Earlier notes" list.
 *
 * An object that carries NEITHER key keeps the historic reading (stringified
 * comparison, which never matched anything) rather than becoming a new
 * silently-true clause.
 */
function matchesWhereValue(got, want) {
  if (want === null) return got == null || got === '';
  if (Array.isArray(want)) return want.some((w) => String(w) === String(got));
  if (want && typeof want === 'object') {
    if (Object.prototype.hasOwnProperty.call(want, 'not')) return !matchesWhereValue(got, want.not);
    if (Object.prototype.hasOwnProperty.call(want, 'matches')) {
      const re = compileMatcher(want.matches);
      return re ? re.test(String(got ?? '')) : false;
    }
  }
  return String(got) === String(want);
}

/** True when every `where` key matches on `row` (loose ==, null-safe). */
export function matchesWhere(row, where) {
  if (!where || typeof where !== 'object') return true;
  for (const [k, want] of Object.entries(where)) {
    if (!matchesWhereValue(getPath(row, k), want)) return false;
  }
  return true;
}

/**
 * `of` as a LADDER: the first of several paths that resolves to a non-empty
 * array wins, and an all-empty ladder is `[]`.
 *
 * A KPI cell whose source is mode-dependent (prod's `keyPoints || decisions`,
 * `followUps || actions`) had exactly one `of:` and therefore had to pick the
 * wrong one for four of the five modes; the alternative was a derive per cell.
 * "First non-empty" is deliberately the only combining rule — a UNION would
 * double-count a row that is in both, and "first present" is what the `||` it
 * ports actually means.
 */
function aggSource(ofExpr, root) {
  if (!Array.isArray(ofExpr)) {
    const one = getPath(root, ofExpr);
    return Array.isArray(one) ? one : [];
  }
  for (const p of ofExpr) {
    const candidate = getPath(root, p);
    if (Array.isArray(candidate) && candidate.length) return candidate;
  }
  return [];
}

/**
 * `{ agg: { op, of, field, where, suffix } }` — count | sum | latest | recent.
 * `of` defaults to "entries" and is itself a context path, so
 * `{op:'count', of:'meds'}` aggregates a selector. `of` may also be a LIST of
 * paths, read as first-non-empty (see `aggSource`).
 */
export function resolveAgg(spec, root) {
  if (!spec) return '';
  const ofExpr = spec.of || 'entries';
  let arr = aggSource(ofExpr, root);
  if (spec.where) arr = arr.filter((r) => matchesWhere(r, spec.where));
  switch (spec.op) {
    case 'count':
      return arr.length;
    case 'recent':
      return Math.min(arr.length, spec.n || arr.length);
    case 'sum':
      return arr.reduce((s, r) => s + num(spec.field ? getPath(r, spec.field) : r), 0);
    case 'latest': {
      const e0 = arr[0] || {};
      return spec.field ? getPath(e0, spec.field) : (e0.name || '');
    }
    default:
      return arr.length;
  }
}

/* ── the two BIND TRANSFORMS (README §4.2) ─────────────────────────────────
 *
 * `map` and `format` are the answer to the two things every blueprint author
 * hand-rolled instead: a per-enum ladder of `when`-gated Badges ("word" ⇒
 * DOCX, "pdf" ⇒ PDF, …), and copy that says `2411724` where it means `2.3 MB`.
 * Both are PURE post-processing of a resolved read — they never reach the
 * context — so they compose with `$`/`bind`/`agg` and with each other, and the
 * Flutter twin can implement them from this file alone.
 *
 * ORDER IS FIXED: `map` first (a value → the label/tone/glyph it stands for),
 * then `format` (a value → how it reads). The reverse is never useful: nobody
 * keys a lookup table on "2.3 MB".
 */

/** en-US pinned, like every other date the kit prints (derives.js DATE_LOCALE). */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The CLOSED format set. Anything else leaves the value untouched. */
export const FORMAT_NAMES = ['number', 'bytes', 'percent', 'ratio', 'date'];

/** 1024-based, one decimal above bytes: `2411724` → "2.3 MB". */
const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export function formatBytes(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  let x = Math.abs(n);
  let u = 0;
  while (x >= 1024 && u < BYTE_UNITS.length - 1) { x /= 1024; u += 1; }
  const rounded = u === 0 ? Math.round(x) : Math.round(x * 10) / 10;
  return `${n < 0 ? '-' : ''}${rounded} ${BYTE_UNITS[u]}`;
}

/** A 0..1 RATIO as whole percent: `0.96` → "96%". Decimals ⇒ use `ratio`. */
export function formatPercent(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${Math.round(n * 100)}%`;
}

/** Two decimals, trailing zeros trimmed: `0.96` → "0.96", `2` → "2". */
export function formatRatio(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return String(Math.round(n * 100) / 100);
}

/**
 * A date value as `d`, an epoch ms number, or an ISO string. A bare
 * `YYYY-MM-DD` is read at LOCAL NOON — parsing it as UTC midnight is what
 * makes a date render as the day before west of Greenwich.
 */
function toDate(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return Number.isFinite(v) ? new Date(v) : null;
  const s = String(v ?? '').trim();
  if (!s) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The MINIMAL date token subset — no library, no locale lookup, so both
 * runtimes print the same string for the same instant:
 *   yyyy · yy · MMMM · MMM · MM · M · dd · d · EEEE · EEE · HH · hh · h · mm · a
 * Everything else in the pattern is a literal (`"MMM d, yyyy"` → "Mar 4, 2026").
 * An unparseable value renders as itself, never "Invalid Date".
 */
const DATE_TOKEN_RE = /yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|hh|h|mm|a/g;

export function formatDate(v, pattern = 'MMM d, yyyy') {
  const d = toDate(v);
  if (!d) return v == null ? v : String(v);
  const h24 = d.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const pad = (n) => String(n).padStart(2, '0');
  return String(pattern).replace(DATE_TOKEN_RE, (t) => {
    switch (t) {
      case 'yyyy': return String(d.getFullYear());
      case 'yy': return pad(d.getFullYear() % 100);
      case 'MMMM': return MONTHS[d.getMonth()];
      case 'MMM': return MONTHS[d.getMonth()].slice(0, 3);
      case 'MM': return pad(d.getMonth() + 1);
      case 'M': return String(d.getMonth() + 1);
      case 'dd': return pad(d.getDate());
      case 'd': return String(d.getDate());
      case 'EEEE': return WEEKDAYS[d.getDay()];
      case 'EEE': return WEEKDAYS[d.getDay()].slice(0, 3);
      case 'HH': return pad(h24);
      case 'hh': return pad(h12);
      case 'h': return String(h12);
      case 'mm': return pad(d.getMinutes());
      case 'a': return h24 < 12 ? 'AM' : 'PM';
      default: return t;
    }
  });
}

/**
 * `{"$":"item.size_bytes","format":"bytes"}` — one closed formatter set
 * (`number · bytes · percent · ratio · date:<pattern>`).
 *
 * An ABSENT value formats to itself, never a fake "0" or a dash: the copy an
 * empty reading should show is `fallback`'s job, and a `when` gate reading the
 * same bind must still see it as absent. An UNKNOWN format name leaves the
 * value untouched (a page written for a newer runtime degrades to the raw
 * value rather than to a red box — §4.5's instinct, applied to a value).
 */
export function formatValue(value, format) {
  if (value == null || value === '') return value;
  const spec = String(format);
  const cut = spec.indexOf(':');
  const name = cut < 0 ? spec : spec.slice(0, cut);
  const arg = cut < 0 ? '' : spec.slice(cut + 1);
  switch (name) {
    case 'number': return formatNumber(value);
    case 'bytes': return formatBytes(value);
    case 'percent': return formatPercent(value);
    case 'ratio': return formatRatio(value);
    case 'date': return formatDate(value, arg || 'MMM d, yyyy');
    default: return value;
  }
}

/**
 * `{"$":"item.file_type","map":{"word":"DOCX","pdf":"PDF"},"fallback":"DOC"}`
 * — an enum to the label / tone / glyph it stands for.
 *
 * Lookup is by exact key, then by the lowercased key (a feed writes "PDF"
 * where the blueprint wrote "pdf", and a widget must not lose its badge over
 * it). A miss — or an absent value — takes `fallback`, and with no `fallback`
 * the raw value passes through, so a map that only relabels SOME values is a
 * valid map.
 *
 * `fallback` alone (no `map`) is the same idea for a value that is merely
 * missing: `{"$":"item.name","fallback":"Untitled"}`.
 */
/**
 * The OUTER half of a 2-D map: pick the table that the `by` read selects.
 *
 * Exact key, then lowercased key — the same ladder the inner lookup uses. A
 * miss takes `then.default` when the author declared one, and otherwise the
 * FIRST declared table: the axis this exists for is content language, every
 * pack is written with the authoring language first, and "a Russian note falls
 * back to the English chrome" is the honest degradation. Falling back to
 * NOTHING would blank every label on the page.
 */
function pickTable(then, key) {
  const k = key == null ? '' : String(key);
  if (k) {
    if (Object.prototype.hasOwnProperty.call(then, k)) return then[k];
    const lower = k.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(then, lower)) return then[lower];
  }
  if (Object.prototype.hasOwnProperty.call(then, 'default')) return then.default;
  for (const name of Object.keys(then)) {
    if (name !== 'default') return then[name];
  }
  return null;
}

/** True for the 2-D form `{by: "<path>", then: {<byValue>: {<ownValue>: …}}}`. */
function isTwoDimensionalMap(table) {
  return Boolean(table) && typeof table === 'object' && !Array.isArray(table)
    && typeof table.by === 'string'
    && Boolean(table.then) && typeof table.then === 'object' && !Array.isArray(table.then);
}

/**
 * A map table as an ORDERED REGEX LADDER:
 * `{"matches": [["meeting|standup|1:1", "Meetings"], ["lecture|class", "Lectures"]]}`.
 *
 * An exact table cannot classify: prod's `modeOf()` is a regex over
 * `briefing_mode || source_type` that folds `lecture` and `lecture_capture`
 * into one chip and reads `1:1` as a meeting, and an enum table has to enumerate
 * every spelling a model might write. FIRST MATCH WINS — the ladder is ordered
 * on purpose, so a narrow pattern can sit above a broad one — and a miss falls
 * through to `fallback` exactly like an exact-table miss.
 */
function matchLadder(value, ladder) {
  const s = String(value ?? '');
  for (const rule of ladder) {
    const [pattern, label] = Array.isArray(rule) ? rule : [rule?.matches, rule?.to];
    const re = compileMatcher(pattern);
    if (re && re.test(s)) return { hit: true, label };
  }
  return { hit: false };
}

export function mapValue(value, table, fallback, root) {
  if (isTwoDimensionalMap(table)) {
    // `by` is read against the CONTEXT, not against the value: the outer axis
    // is a fact about the page (which language the content is in), while the
    // inner one is the value being relabelled.
    return mapValue(value, pickTable(table.then, getPath(root, table.by)), fallback, root);
  }
  if (table && typeof table === 'object' && Array.isArray(table.matches)) {
    if (value != null && value !== '') {
      const { hit, label } = matchLadder(value, table.matches);
      if (hit) return label;
    }
    return fallback !== undefined ? fallback : value;
  }
  const absent = value == null || value === '';
  if (table && typeof table === 'object' && !Array.isArray(table)) {
    if (!absent) {
      const k = String(value);
      if (Object.prototype.hasOwnProperty.call(table, k)) return table[k];
      const lower = k.toLowerCase();
      if (Object.prototype.hasOwnProperty.call(table, lower)) return table[lower];
    }
    return fallback !== undefined ? fallback : value;
  }
  return absent && fallback !== undefined ? fallback : value;
}

/**
 * `{"$":"index","cycle":["b1","b2","b3","b4","b5","b6"]}` — a LADDER walked by
 * position, wrapping at its end.
 *
 * It is the transform an index bind is for: prod keys the topic rail, the
 * avatar plates and the mind-map edges off `TONES[i % 6]`, and neither `map`
 * (exact keys, so a 7th row falls off the table) nor `format` can say that.
 * A non-numeric read, or an empty ladder, leaves the value untouched.
 */
function cycleValue(value, ladder) {
  if (!Array.isArray(ladder) || !ladder.length) return value;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return value;
  const i = Math.trunc(n) % ladder.length;
  return ladder[i < 0 ? i + ladder.length : i];
}

/**
 * `cycle` → `map` → `format`, each a no-op when its key is absent.
 *
 * `cycle` runs FIRST because it turns a position into a value; the other two
 * turn a value into a label and then into copy. The reverse order is never
 * useful — nobody indexes a ladder with "Expiring soon".
 */
function transformValue(value, spec, root) {
  const cycled = spec.cycle !== undefined ? cycleValue(value, spec.cycle) : value;
  const mapped = (spec.map !== undefined || spec.fallback !== undefined)
    ? mapValue(cycled, spec.map, spec.fallback, root)
    : cycled;
  return spec.format !== undefined ? formatValue(mapped, spec.format) : mapped;
}

const INTERP_RE = /\{([^{}]+)\}/g;

/** True when `s` carries at least one `{…}` interpolation slot. */
export function hasInterp(s) {
  return typeof s === 'string' && /\{[^{}]+\}/.test(s);
}

/**
 * `"AI grouped · {medCounts.summary}"` → the context read spliced in. An
 * unresolvable slot renders as '' (never the literal braces) so a partially
 * populated feed degrades to a shorter line instead of leaking IR syntax.
 */
export function interpolate(str, root) {
  if (typeof str !== 'string') return str;
  return str.replace(INTERP_RE, (_all, expr) => {
    const v = getPath(root, expr.trim());
    if (v == null) return '';
    return Array.isArray(v) ? v.filter((x) => x != null).join(', ') : String(v);
  });
}

/**
 * Resolve any IR value spec against the render context:
 *   { bind } · { agg } · "{interp}" · a bare dotted path · a literal.
 *
 * The object form may carry the two TRANSFORMS — `map`/`fallback` and
 * `format` — which post-process the resolved read (in that order) without
 * touching the context. Absent, the value passes through exactly as before.
 *
 * `opts.pathish` (default true) is what makes a bare string like
 * "totals.calories" a data read while "Search medicine" stays a literal: only
 * strings that LOOK like a path (dotted / bracketed, no spaces) are attempted,
 * and an unresolvable one falls back to the literal string.
 */
export function resolveValue(spec, root, opts = {}) {
  if (spec == null) return spec;
  if (typeof spec === 'object' && !Array.isArray(spec)) {
    if (spec.$ !== undefined) return transformValue(getPath(root, spec.$), spec, root);
    if (spec.bind !== undefined) return transformValue(getPath(root, spec.bind), spec, root);
    if (spec.agg !== undefined) {
      const a = transformValue(resolveAgg(spec.agg, root), spec, root);
      return spec.agg.suffix ? `${a}${spec.agg.suffix}` : a;
    }
    return spec;
  }
  if (typeof spec !== 'string') return spec;
  if (hasInterp(spec)) return interpolate(spec, root);
  if (opts.pathish === false) return spec;
  if (!/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_[\]]+|\[[0-9]*\])+$/.test(spec)) return spec;
  const v = getPath(root, spec);
  return v === undefined ? spec : v;
}

/** True when `spec` is the object form of a data reference. */
export function isBindSpec(spec) {
  return Boolean(spec) && typeof spec === 'object' && !Array.isArray(spec)
    && (spec.$ !== undefined || spec.bind !== undefined || spec.agg !== undefined);
}

/**
 * IR TRUTHINESS — what `when` calls "present".
 *
 * JS truthiness plus one rule: an EMPTY ARRAY is absent. A blueprint writing
 * `when: "props.notices"` means "when there are notices", and `[]` reading as
 * true would print an empty section header over nothing. `0` stays falsy (it is
 * the "no value" a count degrades to), which is why the comparison form exists
 * for the cases where zero is a real reading.
 */
export function truthy(v) {
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
}

/** Both sides as finite numbers, or null when either is not comparable. */
function numericPair(a, b) {
  const x = typeof a === 'number' ? a : Number(a);
  const y = typeof b === 'number' ? b : Number(b);
  if (a == null || a === '' || b == null || b === '') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [x, y];
}

/**
 * `when` — the node-level render predicate (README §4.1).
 *
 *   "props.remaining"                                   truthiness of a read
 *   { "path": "props.value", "op": "gt", "value": 0 }   one closed comparison
 *   true / false                                        a literal
 *
 * The op set is deliberately CLOSED and tiny (gt/gte/lt/lte/eq/neq/nonempty/
 * empty/in): `when` is a visibility gate, not an expression engine. Arithmetic
 * belongs inside a robust primitive or a named derive — the moment a blueprint
 * needs `a * b > c` the answer is a better primitive, not a bigger grammar.
 *
 * An UNKNOWN op is false (fail-closed: a node whose condition cannot be read
 * does not render), and a numeric comparison against a non-number is false
 * rather than NaN-true — a null value never wins a threshold.
 */
export function evalWhen(cond, root) {
  if (cond === undefined || cond === null) return true;
  if (typeof cond === 'boolean') return cond;
  if (typeof cond === 'string') return truthy(getPath(root, cond));
  if (typeof cond !== 'object' || Array.isArray(cond)) return truthy(cond);
  if (isBindSpec(cond)) return truthy(resolveValue(cond, root));

  const left = cond.path ? getPath(root, cond.path) : resolveValue(cond.of, root);
  const right = isBindSpec(cond.value) ? resolveValue(cond.value, root) : cond.value;
  switch (cond.op) {
    case 'nonempty': return truthy(left);
    case 'empty': return !truthy(left);
    case 'eq': return String(left ?? '') === String(right ?? '');
    case 'neq': return String(left ?? '') !== String(right ?? '');
    case 'in': return (Array.isArray(right) ? right : []).some((w) => String(w) === String(left));
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const pair = numericPair(left, right);
      if (!pair) return false;
      const [x, y] = pair;
      if (cond.op === 'gt') return x > y;
      if (cond.op === 'gte') return x >= y;
      if (cond.op === 'lt') return x < y;
      return x <= y;
    }
    default:
      return false;
  }
}

/**
 * A `where` clause value may itself be a context path
 * (`{"source_session_id": "page_args.session_id"}`). Resolve those roots
 * before matching; anything else stays a literal.
 */
export function resolveWhere(where, root) {
  if (!where || typeof where !== 'object') return where;
  const out = {};
  for (const [k, v] of Object.entries(where)) out[k] = resolveWhereValue(v, root);
  return out;
}

/**
 * One `where` VALUE resolved against the context. A dotted string rooted at a
 * scope is a read; everything else is a literal.
 *
 * `not` recurses — `{"entry_id": {"not": "page_args.item_id"}}` is the whole
 * point of the negated form, and it is useless if the id it negates stays the
 * literal string "page_args.item_id". `matches` deliberately does NOT recurse:
 * its value is a regex SOURCE, and `"meeting|sync"` must not be mistaken for a
 * path just because a future pattern happens to contain a dot.
 */
function resolveWhereValue(v, root) {
  if (typeof v === 'string' && v.includes('.') && SCOPE_ROOTS.has(pathRoot(v))) {
    return getPath(root, v) ?? null;
  }
  if (v && typeof v === 'object' && !Array.isArray(v)
    && Object.prototype.hasOwnProperty.call(v, 'not')) {
    return { ...v, not: resolveWhereValue(v.not, root) };
  }
  return v;
}
